import { Card, Checkbox, IconButton, LuIcon, Menu, stopPropagation, Text } from "@overmap-ai/blocks"
import { pluralize, type ProcedureInitiative, type Stored } from "@overmap-ai/common"
import {
	selectProceduresOfProcedureInitiative,
	selectProcedureStepsOfProcedureType,
	selectProcedureTypeById,
} from "@overmap-ai/core"
import { cx } from "class-variance-authority"
import { type ComponentProps, memo, useCallback } from "react"

import { useProcedureIntiativeCompletionData } from "../../hooks/procedures"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { formatTimestampDate } from "../../utils/date"
import { ProcedureStepStackedBarChart } from "../Graphs/ProcedureStepStackedBarChart"
import { ProcedureTypeIcon } from "../Icons/ProcedureTypeIcon"
import { computeProcedureInitiativeIndex } from "../IndexLabels/utils"

interface ProcedureInitiativeCardProps extends Omit<ComponentProps<typeof Card>, "children" | "onClick"> {
	procedureInitiative: Stored<ProcedureInitiative>
	onClick?: (procedureInitiative: Stored<ProcedureInitiative>) => void
	onView?: (procedureInitiative: Stored<ProcedureInitiative>) => void
	onDelete?: (procedureInitiative: Stored<ProcedureInitiative>) => void
	selectable?: boolean
	selected?: boolean
	onSelectedChange?: (procedureInitiative: Stored<ProcedureInitiative>, selected: boolean) => void
}

export const ProcedureInitiativeCard = memo<ProcedureInitiativeCardProps>((props) => {
	const {
		className,
		procedureInitiative,
		onClick,
		onView,
		onDelete,
		selectable = false,
		selected = false,
		onSelectedChange,
		...rest
	} = props

	const procedureType = useAppSelector(selectProcedureTypeById(procedureInitiative.procedure_type))!
	const procedures = useAppSelector(selectProceduresOfProcedureInitiative(procedureInitiative.uuid))
	const procedureSteps = useShallowEqaulSelector(
		selectProcedureStepsOfProcedureType(procedureInitiative.procedure_type),
	)
	const procedureStepCompletionData = useProcedureIntiativeCompletionData(procedureInitiative)

	const handleClick = useCallback(() => {
		onClick?.(procedureInitiative)
	}, [procedureInitiative, onClick])

	const handleView = useCallback(() => {
		onView?.(procedureInitiative)
	}, [onView, procedureInitiative])

	const handleDelete = useCallback(() => {
		onDelete?.(procedureInitiative)
	}, [onDelete, procedureInitiative])

	const handleSelectedChange = useCallback(
		(selected: boolean) => {
			onSelectedChange?.(procedureInitiative, selected)
		},
		[onSelectedChange, procedureInitiative],
	)

	return (
		<Card className={cx("oveflow-hidden flex flex-col gap-2", className)} onClick={handleClick} {...rest}>
			<div className="flex h-max w-full items-center justify-between gap-2">
				<div className="flex min-w-0 grow items-center gap-2">
					{selectable && (
						<Checkbox.Root
							checked={selected}
							onCheckedChange={handleSelectedChange}
							onClick={stopPropagation}
							aria-label={`Select ${computeProcedureInitiativeIndex(procedureType, procedureInitiative)}`}
							size="sm"
						>
							<Checkbox.Indicator>
								<LuIcon icon={modelIconMapping.check} />
							</Checkbox.Indicator>
						</Checkbox.Root>
					)}
					<ProcedureTypeIcon procedureTypeId={procedureType.uuid} />
					<Text className="truncate">
						{computeProcedureInitiativeIndex(procedureType, procedureInitiative)}
					</Text>
				</div>

				<Menu.Root modal>
					<Menu.ClickTrigger>
						<IconButton accentColor="base" variant="ghost" size="sm" onClick={stopPropagation}>
							<LuIcon icon={modelIconMapping.more} />
						</IconButton>
					</Menu.ClickTrigger>
					<Menu.Content size="sm">
						<Menu.Item onSelect={handleView}>
							<LuIcon icon={modelIconMapping.expand} />
							View
						</Menu.Item>
						<Menu.Separator />
						<Menu.Item onSelect={handleDelete}>
							<LuIcon icon={modelIconMapping.delete} />
							Delete
						</Menu.Item>
					</Menu.Content>
				</Menu.Root>
			</div>

			<div className="flex size-full overflow-y-auto overflow-x-hidden">
				<ProcedureStepStackedBarChart
					className="pointer-events-none"
					procedureSteps={procedureSteps}
					height="100%"
					width="100%"
					procedureStepCompletionDatas={procedureStepCompletionData}
					hideTooltip
				/>
			</div>

			<div className="flex w-full items-center justify-between gap-2">
				<Text accentColor="base" size="sm" className="whitespace-nowrap">
					{formatTimestampDate(procedureInitiative.submitted_at)}
				</Text>

				<Text accentColor="base" size="sm" className="whitespace-nowrap">
					{procedures.length} {pluralize("procedure", procedures.length)}
				</Text>
			</div>
		</Card>
	)
})
ProcedureInitiativeCard.displayName = "ProcedureInitiativeCard"
